import { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { useRecoilState } from "recoil";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

import { Avatar } from "MVVM/Components/Avatar";
import { AllstarModalState } from "MVVM/State/modals";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";
import { IPlayer, ModalType } from "MVVM/@types";
import { Button, FollowButton } from "MVVM/Components/Button";
import { useUser } from "MVVM/Hooks/useUser";
import { AvatarWrapper, getAvatarBorderLevel } from "MVVM/Utilities/User";
import { EmptyList } from "../EmptyList";
import { randomAvatar } from "MVVM/Utilities/Images";
import { IAllstarUser } from "MVVM/@types/user";
import SteamIcon from "MVVM/Assets/Platforms/steam.svg";
import { ReactComponent as HistoryLogo } from "MVVM/Assets/Nav/nav-history.svg";
import AllstarIcon from "MVVM/Assets/Brand/A-Mark-Light.svg";
import { analytics } from "MVVM/Analytics";
import {
  addAttributionParams,
  getAttributionUserId,
} from "MVVM/Utilities/Attribution";

interface IFollowProps {
  players: IPlayer[];
  matchId: string;
}

interface IFollowModal {
  avatar: string;
  displayName: string;
}

const FollowList = ({ players = [], matchId }: IFollowProps) => {
  const { allstarUser } = useUser();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const setFollowModal = ({ displayName, avatar }: IFollowModal) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: {
        target: displayName,
        avatar: avatar,
        action: "follow",
      },
    });
  };

  const filteredPlayers = players.filter(
    (player) => player?.user?._id !== allstarUser.user?._id,
  );

  if (!filteredPlayers.length)
    return <EmptyList text="Couldn`t find anyone else in this match" />;

  const sortedPlayers = filteredPlayers.sort((a, b) =>
    a.user ? -1 : b.user ? 1 : 0,
  );

  return (
    <Container>
      {sortedPlayers.map((player: IPlayer) => (
        <PlayerItem
          allstarUser={allstarUser}
          matchId={matchId}
          player={player}
          setFollowModal={setFollowModal}
          key={player.gameId}
        />
      ))}
    </Container>
  );
};

interface IPlayerItemProps {
  allstarUser: IAllstarUser;
  player: IPlayer;
  setFollowModal: (arg0: IFollowModal) => void;
  matchId: string;
}

const PlayerItem = ({
  allstarUser,
  player,
  setFollowModal,
  matchId,
}: IPlayerItemProps) => {
  const [hovered, setHovered] = useState(false);
  const { mutateFollowers } = useMutateFollowers();
  const displayName = player?.user?.username || player?.displayName;
  const [linkCopied, setLinkCopied] = useState(false);

  const copyMatchHistoryLink = () => {
    const invitingUser = getAttributionUserId(allstarUser);
    analytics.track("Button - Click", {
      location: "clip",
      locationIdentifier: "inviteToAllstar",
      source: "website",
      invitingUser,
    });
    const baseUrl = `${window.location.origin}/match-history?matchId=${matchId}&playerSearchIdentifier=${player.gameId}&utm_source=clip-experience-invite-by-${invitingUser}`;
    const matchHistoryUrl = addAttributionParams(baseUrl, invitingUser);
    navigator.clipboard.writeText(matchHistoryUrl);
    setLinkCopied(true);
  };

  useEffect(() => {
    if (linkCopied) {
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    }
  }, [linkCopied]);

  if (!displayName) return <></>;

  return (
    <Item>
      <Hoverable
        onMouseOver={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onTouchStart={() => setHovered(!hovered)}
      >
        <HoverableWrapper
          onTouchStart={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <PlayerAvatar player={player} />
          <Flex
            flexDirection="column"
            style={{ gap: 2, marginRight: "auto", marginTop: "-2px" }}
          >
            <Text fontWeight={500}>{displayName}</Text>
            {!player.user && (
              <Text fontWeight={500} color="chalk" fontSize={1}>
                Not on Allstar
              </Text>
            )}
          </Flex>
          <PlayerMenu
            player={player}
            shouldShow={hovered}
            matchId={matchId}
            analyticsProperties={{
              location: "clip",
            }}
          />
        </HoverableWrapper>
      </Hoverable>
      <Box>
        {player.user && (
          <StyledFollowButton
            alignSelf="flex-start"
            allstarUser={allstarUser}
            avatar={player.avatar}
            displayName={player.displayName}
            followUnfollow={mutateFollowers}
            followerData={undefined}
            isModal={false}
            ml={1}
            openModal={true}
            py={5}
            setFollowModal={setFollowModal}
            size="tiny"
            userId={player?.user?._id}
          />
        )}
        {!player.user && (
          <Button
            classification="roundedFilled"
            size="small"
            style={{
              fontSize: "0.9em",
              width: "100%",
              maxWidth: "110px",
              minWidth: "110px",
              paddingLeft: 0,
              paddingRight: 0,
            }}
            onClick={() => {
              copyMatchHistoryLink();
            }}
          >
            {linkCopied ? "Link Copied!" : "Invite"}
          </Button>
        )}
      </Box>
    </Item>
  );
};

export const PlayerMenu = ({
  player,
  shouldShow,
  analyticsProperties,
  showMomentsCta,
  matchId,
  menuAlignment = "left",
}: {
  player: {
    accountName: string | null;
    displayName: string;
    avatar: string;
    gameId: string;
    user: {
      _id: string;
      avatarUrl: string;
      username: string;
    };
  };
  shouldShow: boolean;
  analyticsProperties?: {
    location: string;
  };
  showMomentsCta?: boolean;
  matchId: string;
  menuAlignment?: "left" | "right";
}) => {
  const history = useHistory();
  const { allstarUser } = useUser();
  const { mutateFollowers } = useMutateFollowers();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const [linkCopied, setLinkCopied] = useState(false);
  const setFollowModal = ({ displayName, avatar }: IFollowModal) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: {
        target: displayName,
        avatar: avatar,
        action: "follow",
      },
    });
  };

  const copyMatchHistoryLink = () => {
    const invitingUser = getAttributionUserId(allstarUser);
    analytics.track("Button - Click", {
      location: "clip",
      locationIdentifier: "inviteToAllstar",
      source: "website",
      invitingUser,
    });
    const baseUrl = `${window.location.origin}/match-history?matchId=${matchId}&playerSearchIdentifier=${player.gameId}&utm_source=clip-experience-invite-by-${invitingUser}`;
    const matchHistoryUrl = addAttributionParams(baseUrl, invitingUser);
    navigator.clipboard.writeText(matchHistoryUrl);
    setLinkCopied(true);
  };

  return (
    <Menu
      shouldShow={shouldShow}
      onClick={(e) => e.stopPropagation()}
      menuAlignment={menuAlignment}
    >
      <PlayerHeader>
        <Text variant={"text.labelMedium"}>{player.displayName}</Text>
        {player.accountName !== player.displayName ? (
          <Text variant={"text.labelSmall"} color="chalk">
            {player.accountName}
          </Text>
        ) : (
          showMomentsCta && (
            <Text variant={"text.labelXXSmall"} color="chalk">
              Click to view their moments
            </Text>
          )
        )}
      </PlayerHeader>

      <Flex flexDirection="column" style={{ gap: 2, padding: 12 }}>
        {player.gameId && (
          <PlayerLink
            onClick={() => {
              analytics.track("Button - Click", {
                location: "clip",
                locationIdentifier: "goToMatchHistory",
                source: "website",
                ...analyticsProperties,
              });
              history.push(
                `/match-history?playerSearchIdentifier=${player.gameId}&skipLandingPage=true`,
              );
            }}
          >
            <StyledHistoryLogo
              style={{
                height: "20px",
                marginTop: "-1px",
                width: "20px",
              }}
            />
            <Text fontWeight={500}>Go to Match History</Text>
          </PlayerLink>
        )}
        {/* This prevents non-steam users from leaking through when the graph starts exposing players of other games.
    Will need to implement a more robust way of displaying platform specific links at that time. */}
        {player.gameId && /^(\d{17})$/.test(player.gameId) && (
          <PlayerLink
            onClick={() => {
              analytics.track("Button - Click", {
                location: "clip",
                locationIdentifier: "visitGamePlatform",
                source: "website",
                ...analyticsProperties,
              });
              window.open(
                `http://steamcommunity.com/profiles/${player.gameId}`,
                "_blank",
              );
            }}
          >
            <img
              height={20}
              src={SteamIcon}
              style={{ marginTop: "-1px" }}
              width={20}
            />
            <Text fontWeight={500}>{player?.displayName}</Text>
          </PlayerLink>
        )}
        {player?.user?._id && (
          <PlayerLink
            onClick={() => {
              analytics.track("Button - Click", {
                location: "clip",
                locationIdentifier: "visitAllstarProfile",
                source: "website",
                ...analyticsProperties,
              });
              window.open(`/profile?user=${player.user._id}`, "_blank");
            }}
          >
            <img
              height={20}
              src={AllstarIcon}
              style={{ marginTop: "-1px" }}
              width={20}
            />
            <Text fontWeight={500}>{player?.user?.username}</Text>
          </PlayerLink>
        )}
        {player?.user?._id && (
          <Flex
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <FollowButton
              alignSelf="flex-start"
              allstarUser={allstarUser}
              avatar={player.avatar}
              displayName={player.displayName}
              followUnfollow={mutateFollowers}
              followerData={undefined}
              isModal={false}
              openModal={true}
              setFollowModal={setFollowModal}
              size="tiny"
              style={{ marginTop: 10 }}
              userId={player?.user?._id}
            />
          </Flex>
        )}
        {!player?.user?._id && (
          <Flex
            style={{
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              classification="secondaryRounded"
              size="small"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                minWidth: "125px",
                height: 30,
                marginTop: 10,
              }}
              onClick={() => {
                copyMatchHistoryLink();
              }}
            >
              {linkCopied ? "Link Copied!" : "Invite"}
            </Button>
          </Flex>
        )}
      </Flex>
    </Menu>
  );
};

const PlayerAvatar = ({ player }: { player: IPlayer }) => {
  const avatar =
    player?.user?.avatarUrl || player.avatar || useMemo(randomAvatar, []);

  return (
    <AvatarWrapper size="nav" borderLevel={getAvatarBorderLevel(player.user)}>
      <Avatar
        image={avatar}
        size="nav"
        profileCardUsername={player?.user?.username ?? undefined}
      />
    </AvatarWrapper>
  );
};

const PlayerHeader = styled(Flex)`
  gap: 4px;
  position: relative;
  width: auto;
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  flex-direction: column;
`;

const Hoverable = styled(Flex)`
  margin-right: auto;
`;

const HoverableWrapper = styled(Flex)`
  align-items: center;
  gap: 16px;
  position: relative;
  width: auto;
`;

const Menu = styled(Box)<{
  shouldShow: boolean;
  menuAlignment: "left" | "right";
}>`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.navy};
  display: ${(props) => (props.shouldShow ? "block" : "none")};
  ${({ menuAlignment }) =>
    menuAlignment === "left" ? "left: 20%;" : "right: 20%;"}
  position: absolute;
  top: 70%;
  width: max-content;
  z-index: 500;
`;

const PlayerLink = styled(Box)`
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  font-size: ${({ theme }) => theme.fontSizes[1]};
  gap: 12px;
  padding: 8px;
  text-decoration: none;

  :hover {
    background: ${({ theme }) => theme.colors.navy};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const StyledFollowButton = styled(FollowButton)`
  box-sizing: border-box;
  height: 44px;

  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
`;

const Container = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  padding: 12px;
  width: 100%;
`;

const StyledHistoryLogo = styled(HistoryLogo)`
  fill: ${(props) => props.theme.colors.envy};
`;

const Item = styled(Flex)`
  align-items: center;
  color: reset;
  gap: 18px;
  padding: 12px;
  width: 100%;

  :hover {
    background: ${({ theme }) => theme.colors.navy};
    border-radius: 8px;
    cursor: pointer;
  }
`;

export { FollowList };
